//#region ../src/utils/slider-scroll-guard.ts
/**
* The shipped rule. 12 px (owner decision 2026-09-30, up from 10) is still
* small enough that a deliberate drag feels immediate, and large enough that
* a scroll leaning 40 degrees off vertical that starts with a sideways twitch
* stays a scroll (swept 4-16 px and 30-60 degrees, see the sidecar).
*/
const SLIDER_GUARD_TUNING = Object.freeze({
	intentPx: 12,
	horizontalMaxDeg: 45,
	tapMaxMs: 300
});
/** Live installs per root, so a second install on the same root is shared. */
const installs = /* @__PURE__ */ new WeakMap();
function asRangeInput(target) {
	const el = target;
	return typeof el?.matches === "function" && el.matches("input[type=\"range\"]") ? el : null;
}
/**
* Guard every range input under `root` (the root itself included) against
* touch scrolling, including inputs added after this call.
*
* Listeners sit in the CAPTURE phase on `root`, so the guard runs before any
* listener on the slider itself, whatever the registration order. Installing
* twice on the same root is harmless: the calls share one install, removed
* when the last disposer runs. The shared install keeps the FIRST call's
* `tuning`; a different `tuning` passed to a later call is ignored.
*
* Range inputs inside a shadow root are not reached (their events are
* retargeted to the host); guard the shadow root itself for those.
*
* @param root - usually `document`; an element scopes the guard to its subtree
* @param tuning - the gesture rule; leave it at the default
* @returns a disposer; calling it more than once is harmless
*/
function guardSlidersIn(root, tuning = SLIDER_GUARD_TUNING) {
	const existing = installs.get(root);
	if (existing) existing.count += 1;
	else installs.set(root, {
		count: 1,
		remove: attach(root, tuning)
	});
	let released = false;
	return () => {
		if (released) return;
		released = true;
		const entry = installs.get(root);
		if (!entry) return;
		entry.count -= 1;
		if (entry.count > 0) return;
		installs.delete(root);
		entry.remove();
	};
}
/**
* Guard one range input: `guardSlidersIn(input)`.
*
* @deprecated Prefer one `guardSlidersIn(document)` per page, which also
*   covers sliders created later.
*/
function guardSliderAgainstScroll(input) {
	return guardSlidersIn(input);
}
function attach(root, tuning) {
	let gesture = null;
	/**
	* The slider of a touch tap the guard just replayed, while that tap's touch
	* sequence ends. Chromium fires its own `change` as the `touchend`'s
	* default action (measured 2026-09-30), which would deliver the tap twice.
	*/
	let replayedTap = null;
	const tanMax = Math.tan(tuning.horizontalMaxDeg * Math.PI / 180);
	const onPointerDown = (event) => {
		replayedTap = null;
		const input = asRangeInput(event.target);
		if (!input) return;
		if (event.pointerType === "mouse") return;
		if (gesture && !gesture.awaitingTouchEnd && event.pointerId !== gesture.pointerId) return;
		gesture = {
			input,
			pointerId: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			startTime: event.timeStamp,
			startValue: input.value,
			pendingValue: null,
			intent: "undecided",
			touchSeen: false,
			awaitingTouchEnd: false
		};
	};
	const onTouchStart = (event) => {
		if (gesture && asRangeInput(event.target) === gesture.input) gesture.touchSeen = true;
	};
	const onPointerMove = (event) => {
		if (!gesture || event.pointerId !== gesture.pointerId) return;
		if (gesture.intent !== "undecided") return;
		const dx = Math.abs(event.clientX - gesture.startX);
		const dy = Math.abs(event.clientY - gesture.startY);
		if (Math.max(dx, dy) < tuning.intentPx) return;
		gesture.intent = dy < dx * tanMax ? "horizontal" : "scroll";
	};
	const onPointerUp = (event) => {
		if (!gesture || event.pointerId !== gesture.pointerId) return;
		const ended = gesture;
		gesture = null;
		if (ended.intent === "horizontal") return;
		const tapped = ended.pendingValue;
		if (ended.intent !== "undecided" || event.timeStamp - ended.startTime > tuning.tapMaxMs || tapped === null || tapped === ended.startValue) {
			ended.input.value = ended.startValue;
			return;
		}
		ended.input.value = tapped;
		ended.input.dispatchEvent(new Event("input", { bubbles: true }));
		ended.input.dispatchEvent(new Event("change", { bubbles: true }));
		if (ended.touchSeen) replayedTap = ended.input;
	};
	const onPointerCancel = (event) => {
		if (!gesture || event.pointerId !== gesture.pointerId) return;
		if (gesture.intent === "horizontal") {
			gesture = null;
			return;
		}
		gesture.input.value = gesture.startValue;
		if (gesture.touchSeen) {
			gesture.intent = "scroll";
			gesture.awaitingTouchEnd = true;
		} else gesture = null;
	};
	const onTouchEnd = (event) => {
		const tap = replayedTap;
		if (tap && asRangeInput(event.target) === tap) setTimeout(() => {
			if (replayedTap === tap) replayedTap = null;
		}, 0);
		if (!gesture?.awaitingTouchEnd) return;
		if (asRangeInput(event.target) !== gesture.input) return;
		gesture.input.value = gesture.startValue;
		gesture = null;
	};
	const onValueEvent = (event) => {
		if (event.type === "change" && replayedTap !== null && asRangeInput(event.target) === replayedTap) {
			replayedTap = null;
			event.stopImmediatePropagation();
			return;
		}
		if (!gesture || gesture.intent === "horizontal") return;
		if (asRangeInput(event.target) !== gesture.input) return;
		if (!gesture.awaitingTouchEnd) gesture.pendingValue = gesture.input.value;
		gesture.input.value = gesture.startValue;
		event.stopImmediatePropagation();
	};
	const capture = { capture: true };
	const passive = {
		capture: true,
		passive: true
	};
	const listeners = [
		[
			"pointerdown",
			onPointerDown,
			capture
		],
		[
			"pointermove",
			onPointerMove,
			capture
		],
		[
			"pointerup",
			onPointerUp,
			capture
		],
		[
			"pointercancel",
			onPointerCancel,
			capture
		],
		[
			"touchstart",
			onTouchStart,
			passive
		],
		[
			"touchend",
			onTouchEnd,
			passive
		],
		[
			"touchcancel",
			onTouchEnd,
			passive
		],
		[
			"input",
			onValueEvent,
			capture
		],
		[
			"change",
			onValueEvent,
			capture
		]
	];
	for (const [type, handler, options] of listeners) root.addEventListener(type, handler, options);
	return () => {
		gesture = null;
		replayedTap = null;
		for (const [type, handler, options] of listeners) root.removeEventListener(type, handler, options);
	};
}
//#endregion
export { SLIDER_GUARD_TUNING, guardSliderAgainstScroll, guardSlidersIn };
